import { createStore } from "../../util/zustand";
import { XIcon } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { v4 as uuidv4 } from "uuid";
import { createPortal } from "react-dom";

const DEFAULT_TOAST_DURATION = 5000;
const HOVER_RESUME_DURATION = 3000;

type ToastCTA = {
    text: string;
    action: () => void;
};

type Position = "bottom" | "right";

type Toast = {
    id: string;
    text: string;
    position: Position;
    action?: ToastCTA;
    timeoutId: NodeJS.Timeout;
    duplicate: number; // 1 means first, 2 means second, etc.
};

type ToastOptions = {
    duration?: number;
    position?: Position;
    action?: ToastCTA;
};

const store = createStore<{ toasts: Toast[] }>({ toasts: [] });

function show(text: string, options?: ToastOptions) {
    const id = uuidv4();
    const duration = options?.duration ?? DEFAULT_TOAST_DURATION;
    const { toasts } = store.getState();

    // Find all toasts with the same text and get the highest duplicate value
    let maxDuplicate = 0;
    for (const toast of toasts) {
        if (toast.text === text && toast.duplicate > maxDuplicate) {
            maxDuplicate = toast.duplicate;
        }
    }

    const newDuplicate = maxDuplicate + 1;

    // Remove all previous toasts with the same text
    const filteredToasts = toasts.filter(t => t.text !== text);

    const toast: Toast = {
        id,
        text: text,
        position: options?.position || "bottom",
        action: options?.action,
        timeoutId: setTimeout(() => {
            dismiss(id);
        }, duration),
        duplicate: newDuplicate,
    };

    store.setState({
        toasts: [...filteredToasts, toast],
    });
}

function dismiss(id: string) {
    store.setState(prev => ({
        toasts: prev.toasts.filter(toast => toast.id !== id),
    }));
}

const ENTER_CLASS = {
    bottom: "starting:translate-y-4 starting:opacity-0",
    right: "starting:translate-x-4 starting:opacity-0",
} satisfies Record<Position, string>;

const ToastContent = (props: { toast: Toast }) => {
    const { toast } = props;

    return (
        <div
            className={twMerge(
                "pointer-events-auto w-fit max-w-full rounded bg-stone-950 select-none",
                "transition duration-500 ease-out",
                ENTER_CLASS[toast.position],
            )}
            onMouseEnter={() => clearTimeout(toast.timeoutId)}
            onMouseLeave={() => {
                toast.timeoutId = setTimeout(() => {
                    dismiss(toast.id);
                }, HOVER_RESUME_DURATION);
            }}
        >
            <div className="flex items-center justify-between px-3 py-1 text-white">
                <div className="flex">
                    <h3>
                        {toast.text}
                        {/* For display, append (x) if duplicate > 1 */}
                        {toast.duplicate > 1 ? ` (${toast.duplicate})` : ""}
                    </h3>

                    {toast.action && (
                        <div
                            className="ml-3 cursor-pointer font-bold text-white underline"
                            onClick={() => {
                                toast.action?.action();
                                dismiss(toast.id);
                            }}
                        >
                            {toast.action?.text}
                        </div>
                    )}
                </div>

                <XIcon
                    className="ml-1.5 size-4 text-stone-400 hover:text-stone-50"
                    onClick={() => {
                        dismiss(toast.id);
                    }}
                />
            </div>
        </div>
    );
};

// Main component that goes at the highest level of the app as <MagicToast.Container /> (currently in App.tsx)
function Container() {
    const toasts = store.use(s => s.toasts);

    const bottomToasts = toasts.filter(toast => toast.position === "bottom");
    const rightToasts = toasts.filter(toast => toast.position === "right");

    return createPortal(
        <>
            {/* Top Right toasts container */}
            <div className="pointer-events-none fixed inset-0 z-100001 flex items-start justify-end px-3 py-2">
                <div className="flex w-full flex-col items-end space-y-4">
                    {rightToasts.map(toast => (
                        <ToastContent key={toast.id} toast={toast} />
                    ))}
                </div>
            </div>

            {/* Bottom toasts container */}
            <div className="pointer-events-none fixed inset-0 z-100001 mb-12 flex items-end px-6 py-6">
                <div className="flex w-full flex-col items-center space-y-4">
                    {bottomToasts.map(toast => (
                        <ToastContent key={toast.id} toast={toast} />
                    ))}
                </div>
            </div>
        </>,
        document.body,
    );
}

export default {
    Container,
    show,
    dismiss,
};
